import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { getUnifiedNotificationAnalytics, getUnifiedNotificationStats, getWorkflowHealth } from '../../utils/adminApi';
import { WORKFLOW_REGISTRY } from './workflowConfig';
import '../../styles/NotificationAnalytics.css';

// ─── Funnel Bar (reusable, same pattern as OverviewTab) ──────────────────────
const FunnelBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 1000) / 10 : 0;
  return (
    <div className="notif-funnel-row">
      <div className="notif-funnel-label">{label}</div>
      <div className="notif-funnel-bar-track">
        <div className="notif-funnel-bar-fill" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <div className="notif-funnel-value">
        {count.toLocaleString()} <span className="notif-funnel-pct">({pct}%)</span>
      </div>
    </div>
  );
};

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="notif-analytics-tooltip">
      <div className="notif-analytics-tooltip-label">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="notif-analytics-tooltip-row" style={{ color: entry.fill }}>
          <span>{entry.name}:</span>
          <span>{(entry.value || 0).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Engagement Bar ───────────────────────────────────────────────────────────
const EngagementBar = ({ name, rate }) => {
  const pct = Math.round(rate * 100 * 10) / 10;
  const color = rate > 0.5 ? '#4ade80' : rate >= 0.2 ? '#fbbf24' : '#f87171';
  return (
    <div className="notif-engagement-row">
      <div className="notif-engagement-name">{name}</div>
      <div className="notif-engagement-track">
        <div className="notif-engagement-fill" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
      </div>
      <div className="notif-engagement-pct" style={{ color }}>{pct}%</div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AnalyticsTab = () => {
  const [channel, setChannel] = useState('all');
  const [timeRange, setTimeRange] = useState('7d');
  const [analyticsData, setAnalyticsData] = useState([]);
  const [stats, setStats] = useState(null);
  const [workflowEngagement, setWorkflowEngagement] = useState([]);
  const [loading, setLoading] = useState(true);

  // Map UI time range to API param
  const apiTimeRange = timeRange === '7d' ? 'week' : timeRange === '30d' ? 'month' : 'quarter';
  const apiChannel = channel === 'all' ? null : channel;

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, timeRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [analytics, statsData, healthData] = await Promise.all([
        getUnifiedNotificationAnalytics(apiTimeRange, apiChannel),
        getUnifiedNotificationStats(apiTimeRange),
        getWorkflowHealth(),
      ]);

      setAnalyticsData(analytics || []);
      setStats(statsData);

      // Compute engagement rate per workflow
      const engagementRows = WORKFLOW_REGISTRY.map((wf) => {
        let matching = [];
        if (wf.channel === 'push') {
          matching = (healthData.pushes || []).filter(p => p.category === wf.matchValue);
        } else {
          matching = (healthData.emails || []).filter(e =>
            e.template_name === wf.matchValue || e.template_key === wf.matchValue
          );
        }

        const total = matching.length;
        let opened = 0;
        let delivered = 0;

        if (wf.channel === 'push') {
          const deliveredStatuses = new Set(['delivered', 'opened', 'clicked']);
          matching.forEach(p => {
            const logs = p.push_delivery_logs || [];
            const isDelivered = logs.some(l => deliveredStatuses.has(l.status));
            if (isDelivered) {
              delivered++;
              opened++; // treat delivered push as "engaged" since there's no open concept
            }
          });
        } else {
          const deliveredStatuses = new Set(['delivered', 'opened', 'clicked']);
          matching.forEach(e => {
            if (deliveredStatuses.has(e.status)) delivered++;
            if (e.status === 'opened' || e.status === 'clicked') opened++;
          });
        }

        const engagementRate = delivered > 0 ? opened / delivered : 0;
        return { id: wf.id, name: wf.name, engagementRate, total };
      });

      // Sort descending by engagement rate
      engagementRows.sort((a, b) => b.engagementRate - a.engagementRate);
      setWorkflowEngagement(engagementRows);
    } catch (err) {
      console.error('Error loading analytics data:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Channel comparison: sum daily values ──────────────────────────────────
  const channelStats = analyticsData.reduce(
    (acc, d) => {
      acc.emailSent += d.emailSent || 0;
      acc.emailDelivered += d.emailDelivered || 0;
      acc.emailOpened += d.emailOpened || 0;
      acc.pushSent += d.pushSent || 0;
      acc.pushDelivered += d.pushDelivered || 0;
      acc.pushOpened += d.pushOpened || 0;
      return acc;
    },
    { emailSent: 0, emailDelivered: 0, emailOpened: 0, pushSent: 0, pushDelivered: 0, pushOpened: 0 }
  );

  const emailDeliveryRate = channelStats.emailSent > 0
    ? Math.round((channelStats.emailDelivered / channelStats.emailSent) * 1000) / 10
    : 0;
  const emailOpenRate = channelStats.emailDelivered > 0
    ? Math.round((channelStats.emailOpened / channelStats.emailDelivered) * 1000) / 10
    : 0;
  const pushDeliveryRate = channelStats.pushSent > 0
    ? Math.round((channelStats.pushDelivered / channelStats.pushSent) * 1000) / 10
    : 0;
  const pushOpenRate = channelStats.pushDelivered > 0
    ? Math.round((channelStats.pushOpened / channelStats.pushDelivered) * 1000) / 10
    : 0;

  if (loading) {
    return <div className="notif-analytics-loading">Loading analytics...</div>;
  }

  return (
    <div className="notif-analytics">
      {/* ── Controls ── */}
      <div className="notif-analytics-controls">
        <div className="notif-analytics-pills">
          {[
            { value: 'all', label: 'All Channels' },
            { value: 'email', label: '📧 Email' },
            { value: 'push', label: '📱 Push' },
          ].map(opt => (
            <button
              key={opt.value}
              className={`notif-analytics-pill ${channel === opt.value ? 'active' : ''}`}
              onClick={() => setChannel(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="notif-analytics-pills">
          {['7d', '30d', '90d'].map(r => (
            <button
              key={r}
              className={`notif-analytics-pill ${timeRange === r ? 'active' : ''}`}
              onClick={() => setTimeRange(r)}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* ── 1. Send Volume Over Time ── */}
      <div className="notif-analytics-card notif-analytics-full">
        <h3 className="notif-card-title">Send Volume Over Time</h3>
        {analyticsData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={analyticsData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="date"
                tick={{ fill: '#9ca3af', fontSize: 10 }}
                tickFormatter={d => (d ? d.slice(5) : '')}
              />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} />
              <Tooltip content={<DarkTooltip />} />
              <Legend wrapperStyle={{ fontSize: '0.75rem', color: '#374151' }} />
              {(channel === 'all' || channel === 'email') && (
                <Bar dataKey="emailSent" name="Email" fill="#a78bfa" stackId="sent" radius={[2, 2, 0, 0]} />
              )}
              {(channel === 'all' || channel === 'push') && (
                <Bar dataKey="pushSent" name="Push" fill="#22d3ee" stackId="sent" radius={[2, 2, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="notif-analytics-empty">No data for this period</div>
        )}
      </div>

      {/* ── 2-col grid: Funnel + Engagement ── */}
      <div className="notif-analytics-grid">
        {/* 2. Delivery Funnel */}
        <div className="notif-analytics-card">
          <h3 className="notif-card-title">Delivery Funnel</h3>
          {stats ? (
            <div className="notif-funnel">
              <FunnelBar label="Sent" count={stats.funnel.sent} total={stats.funnel.sent} color="#a78bfa" />
              <FunnelBar label="Delivered" count={stats.funnel.delivered} total={stats.funnel.sent} color="#22d3ee" />
              <FunnelBar label="Opened" count={stats.funnel.opened} total={stats.funnel.sent} color="#fbbf24" />
              <FunnelBar label="Clicked" count={stats.funnel.clicked} total={stats.funnel.sent} color="#f472b6" />
              <FunnelBar label="Bounced" count={stats.funnel.bounced} total={stats.funnel.sent} color="#f87171" />
            </div>
          ) : (
            <div className="notif-analytics-empty">No funnel data</div>
          )}
        </div>

        {/* 3. Engagement by Notification Type */}
        <div className="notif-analytics-card">
          <h3 className="notif-card-title">Engagement by Notification Type</h3>
          {workflowEngagement.length > 0 ? (
            <div className="notif-analytics-engagement-list">
              {workflowEngagement.map(wf => (
                <EngagementBar key={wf.id} name={wf.name} rate={wf.engagementRate} />
              ))}
            </div>
          ) : (
            <div className="notif-analytics-empty">No engagement data</div>
          )}
        </div>
      </div>

      {/* ── 4. Channel Comparison ── */}
      <div className="notif-channel-comparison">
        <div className="notif-channel-card">
          <div className="notif-channel-header">
            <span className="notif-channel-icon">📧</span>
            <span className="notif-channel-title">Email</span>
          </div>
          <div className="notif-channel-stats">
            <div className="notif-channel-stat">
              <div className="notif-channel-stat-value">{channelStats.emailSent.toLocaleString()}</div>
              <div className="notif-channel-stat-label">Total Sent</div>
            </div>
            <div className="notif-channel-stat">
              <div className="notif-channel-stat-value">{emailDeliveryRate}%</div>
              <div className="notif-channel-stat-label">Delivery Rate</div>
            </div>
            <div className="notif-channel-stat">
              <div className="notif-channel-stat-value">{emailOpenRate}%</div>
              <div className="notif-channel-stat-label">Open Rate</div>
            </div>
          </div>
        </div>
        <div className="notif-channel-card">
          <div className="notif-channel-header">
            <span className="notif-channel-icon">📱</span>
            <span className="notif-channel-title">Push</span>
          </div>
          <div className="notif-channel-stats">
            <div className="notif-channel-stat">
              <div className="notif-channel-stat-value">{channelStats.pushSent.toLocaleString()}</div>
              <div className="notif-channel-stat-label">Total Sent</div>
            </div>
            <div className="notif-channel-stat">
              <div className="notif-channel-stat-value">{pushDeliveryRate}%</div>
              <div className="notif-channel-stat-label">Delivery Rate</div>
            </div>
            <div className="notif-channel-stat">
              <div className="notif-channel-stat-value">{pushOpenRate}%</div>
              <div className="notif-channel-stat-label">Open Rate</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;
